import { useId } from 'react';
import type { JSX, ReactNode } from 'react';
import { STATUS_TONES, cx } from './styles';
import type { StatusLevel } from './styles';

export interface ProgressBarProps {
  /**
   * `null` / 不传 = **不确定进度**（还不知道总量，例如"正在读取参数 第 3 批"）；
   * `0` = 确实为零。二者必须分开：把"不知道"画成 0% 的空条，用户会以为卡住了。
   */
  value?: number | null;
  min?: number;
  max?: number;
  level?: StatusLevel;
  /** 显示在进度条上方的标签 */
  label?: ReactNode;
  /** 给读屏器的**具体读数**（`'12.4 V'`、`'65 %'`）。没有它读屏器只会念百分比 */
  valueText?: string;
  size?: 'sm' | 'md';
  className?: string;
}

function clamp(value: number, lo: number, hi: number): number {
  if (value < lo) return lo;
  if (value > hi) return hi;
  return value;
}

/**
 * 进度条。确定进度用 `value`，不确定进度用 `value={null}`。
 */
export function ProgressBar({
  value,
  min,
  max,
  level = 'neutral',
  label,
  valueText,
  size = 'md',
  className,
}: ProgressBarProps): JSX.Element {
  const labelId = `progress-${useId().replaceAll(':', '')}`;
  const tone = STATUS_TONES[level];
  const indeterminate = value === undefined || value === null;
  const lo = min ?? 0;
  const hi = max ?? 100;
  const span = hi - lo;

  /*
   * 夹取只影响**画出来的比例**与 aria 数值：进度条是"占了多少"的可视化，
   * 超出 100% 在几何上没有位置。真实数值应该由旁边一个数值读数负责显示 ——
   * 这里不静默修改调用方传进来的语义（`valueText` 仍是原文），只是不画到框外。
   */
  const clamped = !indeterminate && span > 0 ? clamp(value, lo, hi) : lo;
  const percent = !indeterminate && span > 0 ? Math.round(((clamped - lo) / span) * 100) : 0;

  return (
    <div className={className}>
      {label === undefined && (indeterminate || valueText === undefined) ? null : (
        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
          {label === undefined ? <span /> : (
            <span id={labelId} className="text-fg-muted">
              {label}
            </span>
          )}
          {indeterminate ? null : (
            <span className="tabular-nums text-fg-muted">{valueText ?? `${percent} %`}</span>
          )}
        </div>
      )}

      <div
        role="progressbar"
        {...(label === undefined ? { 'aria-label': '进度' } : { 'aria-labelledby': labelId })}
        /*
         * 不确定进度**不给** aria-valuenow —— 这正是"进度未知"的标准表达方式，
         * 读屏器不会念出一个凭空捏造的百分比。
         */
        aria-valuemin={indeterminate ? undefined : lo}
        aria-valuemax={indeterminate ? undefined : hi}
        aria-valuenow={indeterminate ? undefined : clamped}
        {...(valueText === undefined ? {} : { 'aria-valuetext': valueText })}
        className={cx(
          'w-full overflow-hidden rounded-full bg-surface-3',
          size === 'sm' ? 'h-1.5' : 'h-2',
        )}
      >
        <div
          className={cx(
            'h-full',
            tone.fill,
            indeterminate && 'w-full animate-pulse opacity-40 motion-reduce:animate-none',
          )}
          {...(indeterminate ? {} : { style: { width: `${percent}%` } })}
        />
      </div>
    </div>
  );
}
